import React, {Component} from "react";
import styled from "styled-components";
import Typography from "@material-ui/core/Typography";
import Card from "@material-ui/core/Card";
import {throttle} from "throttle-debounce";
import PropTypes from "prop-types";
import {withStyles} from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Paper from "@material-ui/core/Paper";
import MenuItem from "@material-ui/core/MenuItem";
import CloseIcon from "@material-ui/icons/Close";
import Snackbar from "@material-ui/core/Snackbar";
import IconButton from "@material-ui/core/IconButton";
import SearchIcon from "@material-ui/icons/Search";
import AsyncSelect from "react-select/lib/Async";
import {getAddressByText} from "../../services/api.js";
import {AUTOCOMPLETE_PLACEHOLDER, ERROR_GEOLOCATION_LABEL, SEARCH_TEXT_LOADING} from "../../text.js";
import MenuIcon from "@material-ui/icons/Menu";
import Button from "@material-ui/core/Button";
import CardMedia from "@material-ui/core/es/CardMedia";

const minCharAutoComplete = process.env.REACT_APP_MIN_CHAR_AUTOCOMPLETE;
const minDelayAutocomplete = process.env.REACT_APP_MIN_DELAY_AUTOCOMPLETE;

const createOption = label => ({
  label,
  value: label
});

function CardMediaLink(props) {
  return <CardMedia button component="a" {...props} />;
}

const promiseOptions = inputValue => {
  if (
      inputValue.length > minCharAutoComplete &&
      !Number(inputValue.replace(/ /g, ""))
  ) {
    return getAddressByText(inputValue).then(address => {
      const addressFormat = [];
      address.data.forEach(data =>
          addressFormat.push({
            label: `${data.rue} ${data.ville}`,
            data: data
          })
      );
      return addressFormat;
    });
  } else {
    return new Promise((resolve, reject) => {
      resolve(null);
    });
  }
};

const promiseOptionsThrottle = throttle(
    minDelayAutocomplete,
    (searchTerm, callback) => {
      promiseOptions(searchTerm)
          .then(result => {
            callback(result);
          })
          .catch(error => callback(null));
    }
);

const AutoCompleteContainer = styled.div`
  position: relative;
  width: 100vw;
  @media only screen and (min-width: 768px) {
    width: 41em;
  }
`;

const styles = theme => ({
  root: {
    display: "flex",
    alignItems: "center",
    flexGrow: 1,
    padding: "0.9em",
    [theme.breakpoints.up("sm")]: {},
    [theme.breakpoints.down("420")]: {}
  },
  input: {
    display: "flex",
    padding: 0,
    [theme.breakpoints.up("sm")]: {}
  },
  valueContainer: {
    display: "flex",
    flex: 1,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    alignItems: "center",
    [theme.breakpoints.up("sm")]: {}
  },
  asyncselect: {
    width: "24.7em",
    [theme.breakpoints.down("768")]: {
      width: "45vw"
    },
    [theme.breakpoints.down("420")]: {
      width: "40vw"
    }
  },
  noOptionsMessage: {
    padding: `${theme.spacing.unit}px ${theme.spacing.unit * 2}px`
  },
  singleValue: {
    fontSize: 16,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    [theme.breakpoints.up("sm")]: {}
  },
  placeholder: {
    position: "absolute",
    left: 2,
    fontSize: 16,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    width: "100%"
  },
  paper: {
    position: "absolute",
    zIndex: 1,
    marginTop: theme.spacing.unit,
    left: 0,
    right: 0
  },
  logoCeleste: {
    padding: '0px 60px',
    height: '48px',
    backgroundImage: "url('/logo_celeste.png')",
    backgroundSize: '110px',
    backgroundRepeat: 'no-repeat',
    backgroundPositionY: '1px',
    [theme.breakpoints.down("420")]: {
      padding: '0px 28px',
      backgroundImage: "url('/etoile1.png')",
      backgroundSize: '45px',
      backgroundRepeat: 'no-repeat',
      backgroundPositionX: '8px',
    }
  }
});

function NoOptionsMessage(props) {
  return (
      <Typography
          color="textSecondary"
          className={props.selectProps.classes.noOptionsMessage}
          {...props.innerProps}
      >
        {props.children}
      </Typography>
  );
}

function inputComponent({inputRef, ...props}) {
  return <div ref={inputRef} {...props} />;
}

function Control(props) {
  return (
      <TextField
          fullWidth
          InputProps={{
            inputComponent,
            inputProps: {
              className: props.selectProps.classes.input,
              inputRef: props.innerRef,
              children: props.children,
              ...props.innerProps
            }
          }}
          {...props.selectProps.textFieldProps}
      />
  );
}

function Option(props) {
  return (
      <MenuItem
          buttonRef={props.innerRef}
          selected={props.isFocused}
          component="div"
          style={{
            fontWeight: props.isSelected ? 500 : 400,
            whiteSpace: "nowrap",
            textOverflow: "ellipsis",
            display: "block",
            fontSize: 16
          }}
          {...props.innerProps}
      >
        {props.children}
      </MenuItem>
  );
}

function Placeholder(props) {
  return (
      <Typography
          color="textSecondary"
          className={props.selectProps.classes.placeholder}
          {...props.innerProps}
      >
        {props.children}
      </Typography>
  );
}

function SingleValue(props) {
  return (
      <Typography
          className={props.selectProps.classes.singleValue}
          {...props.innerProps}
      >
        {props.children}
      </Typography>
  );
}

function ValueContainer(props) {
  return (
      <div className={props.selectProps.classes.valueContainer}>
        {props.children}
      </div>
  );
}

function Menu(props) {
  return (
      <Paper
          square
          className={props.selectProps.classes.paper}
          {...props.innerProps}
      >
        {props.children}
      </Paper>
  );
}

const components = {
  Control,
  Menu,
  NoOptionsMessage,
  Option,
  Placeholder,
  SingleValue,
  ValueContainer,
  DropdownIndicator: null
};

class AutoComplete extends Component {
  constructor(props) {
    super(props);
    this.state = {
      inputValue: "",
      value: []
    };
    this.userChoice = false;
    this.autocomplete = React.createRef();
  }

  componentWillReceiveProps(nextProps) {
    if (nextProps.currentAddressValue) {
      this.setState({
        inputValue: "",
        value: [createOption(nextProps.currentAddressValue)]
      });
    } else {
      this.setState({
        inputValue: "",
        value: []
      });
    }
  }

  handleChange = (value, actionMeta) => {
    this.userChoice = actionMeta.action === "select-option";
    if (this.userChoice) {
      this.props.onUserSelectAddress(value);
    } else {
      this.setState({value});
    }
  };

  handleInputChange = inputValue => {
    this.setState({inputValue});
  };

  onclick = () => {
    this.handleBlur();
    if (this.state.value[0]) {
      const value = this.state.value[0];
      this.setState(
          {
            inputValue: value.value,
            value: []
          });
    }
  };

  handleKeyDown = event => {
    const {inputValue} = this.state;
    if (
        this.autocomplete.current.state.loadedOptions.length === 0 &&
        event.key === "Enter"
    ) {
      this.setState(
          {
            inputValue: inputValue,
            value: [createOption(inputValue)]
          },
          () => {
            this.btnSearch.focus();
            this.btnSearch.click();
          }
      );
      event.preventDefault();
    } else {
      switch (event.key) {
        case "Tab":
          this.setState({
            inputValue: inputValue,
            value: [createOption(inputValue)]
          });
          this.btnSearch.focus();
          event.preventDefault();
          break;
        default:
          return;
      }
    }
  };

  handleFocus = () => {
    this.userChoice = false;
  };

  handleBlur = () => {
    const {inputValue} = this.state;
    if (!this.userChoice && inputValue) {
      this.setState({
        inputValue: inputValue,
        value: [createOption(inputValue)]
      });
    }
    this.userChoice = false;
  };

  render() {
    const {classes, theme, toggleMenuAction} = this.props;
    const {inputValue, value} = this.state;
    const selectStyles = {
      input: base => ({
        ...base,
        color: theme.palette.text.primary,
        "& input": {
          font: "inherit"
        }
      })
    };

    return (
        <AutoCompleteContainer>
          <Card style={{overflow: "visible"}}>
            <div className={classes.root}>
              <CardMediaLink
                  className={classes.logoCeleste}
                  title="Celeste.fr"
                  href={"https://www.celeste.fr/"}
                  target={"_blank"}
              />

              <AsyncSelect
                  className={classes.asyncselect}
                  ref={this.autocomplete}
                  onBlur={this.handleBlur}
                  loadOptions={promiseOptionsThrottle}
                  classes={classes}
                  styles={selectStyles}
                  components={components}
                  onFocus={this.onclick}
                  onChange={this.handleChange}
                  onInputChange={this.handleInputChange}
                  onKeyDown={this.handleKeyDown}
                  inputValue={inputValue}
                  value={value}
                  placeholder={AUTOCOMPLETE_PLACEHOLDER}
                  noOptionsMessage={() => null}
                  loadingMessage={() => SEARCH_TEXT_LOADING}
              />

              <IconButton
                  buttonRef={ref => (this.btnSearch = ref)}
                  onClick={() =>
                      this.props.OpenModalAddress(
                          this.state.value && this.state.value.label
                              ? this.state.value.label
                              : this.state.value[0].label
                      )
                  }
                  color="primary"
                  disabled={
                    this.state.value[0]
                        ? this.state.value[0].label.length <= minCharAutoComplete
                        : this.state.value && this.state.value.label
                        ? this.state.value.label.length <= minCharAutoComplete
                        : this.state.inputValue.length <= minCharAutoComplete
                  }
              >
                <SearchIcon/>
              </IconButton>
              <Button onClick={toggleMenuAction(true)}><MenuIcon/></Button>
            </div>
          </Card>

          <Snackbar
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left"
              }}
              open={this.state.openErrorGeolocation}
              autoHideDuration={6000}
              onClose={this.handleCloseGeolocationError}
              message={<span>{ERROR_GEOLOCATION_LABEL}</span>}
              action={[
                <IconButton
                    disableRipple
                    key="close"
                    aria-label="Close"
                    color="inherit"
                    onClick={this.handleCloseGeolocationError}
                >
                  <CloseIcon/>
                </IconButton>
              ]}
          />
        </AutoCompleteContainer>
    );
  }
}

AutoComplete.propTypes = {
  classes: PropTypes.object.isRequired,
  theme: PropTypes.object.isRequired
};

export default withStyles(styles, {withTheme: true})(AutoComplete);
